import { useCallback } from 'react'
import {
  useDispatch,
  usePartialSubscription,
  useSettings,
  useShippingAddress,
  useSignupState
} from '.'
import {
  clearBenefits,
  clearValidations,
  fetchShippingAddress,
  updatePartialSubscription,
  updateSignupSession
} from '../actions'
import { ShippingAddress, ShippingSupportedServices } from '../types'
import { getPaperAddon } from '../lib/helpers'
import { updateStateData } from '../stateData/updateStateData'

export const usePaperShippingValidation = () => {
  const dispatch = useDispatch()
  const { validations: validationsState } = useSignupState()
  const partialSubscriptionState = usePartialSubscription()
  const shippingAddressState = useShippingAddress()
  const { enableInkPaperPlans } = useSettings().data || {}

  const validatePaperAddress = useCallback(
    async (shippingAddress: ShippingAddress) => {
      const errorCode = validationsState?.data?.errorCode
      const optedIn = getPaperAddon(partialSubscriptionState.data.addons)
        ?.optedIn

      const isAddressInvalid = !shippingAddress?.supportedServices?.includes(
        ShippingSupportedServices.INSTANT_PAPER
      )
      const isAddressValid = !isAddressInvalid

      if (isAddressInvalid && optedIn && enableInkPaperPlans) {
        await dispatch(
          updateStateData('validations', {
            data: {
              errorCode: enableInkPaperPlans
                ? 'paper_unavailable'
                : 'unsupported_paper_address'
            }
          })
        )
      } else if (isAddressInvalid && !!errorCode) {
        await dispatch(clearValidations())
        await dispatch(
          updateStateData('validations', {
            data: {
              errorCode
            }
          })
        )
      } else if (isAddressValid && !!errorCode) {
        await dispatch(
          updatePartialSubscription({
            paper_opt_in: true
          })
        )
        await dispatch(clearBenefits())
        await dispatch(clearValidations())
      } else if (isAddressValid && optedIn) {
        await dispatch(
          updatePartialSubscription({
            paper_opt_in: true
          })
        )
        await dispatch(clearBenefits())
      }
    },
    [
      dispatch,
      enableInkPaperPlans,
      partialSubscriptionState.data,
      validationsState?.data?.errorCode
    ]
  )

  const onCloseShippingModal = useCallback(async () => {
    await dispatch(updateSignupSession({ showShippingModal: false }))
    const shippingAddress = shippingAddressState.data

    await validatePaperAddress(shippingAddress)
  }, [dispatch, shippingAddressState.data, validatePaperAddress])

  const onSaveShippingAddressSuccess = useCallback(
    async (setNotAddedOnShippingStep?: boolean) => {
      await dispatch(
        updateSignupSession({
          showShippingModal: false,
          ...(setNotAddedOnShippingStep
            ? { shippingAddedOnShippingStep: false }
            : {})
        })
      )
      const updatedShippingAddress = (await dispatch(
        fetchShippingAddress()
      )) as ShippingAddress

      await validatePaperAddress(updatedShippingAddress)
    },
    [dispatch, validatePaperAddress]
  )

  return {
    validatePaperAddress,
    onCloseShippingModal,
    onSaveShippingAddressSuccess
  }
}
